import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'app/components/Button';
import Dropdown from 'app/components/Dropdown';
import { formatTimer, parseIntOrNull } from 'app/utils/numbers';
import InputNumber from 'app/components/Input/InputNumber';
import InputTime from 'app/components/Input/InputTime';

import style from './style.scss';

interface Props {
  value: number | null;
  type: 'timer' | 'duration';
  title: string;
  example?: string;
  displayedValueOffset?: number;
  onChange: (value: number | null) => void;
  onCancel: () => void;
  disabled?: boolean;
  onlyLabel?: boolean;
}

const TimerDurationDropdownInput: FC<Props> = ({
  value,
  type,
  title,
  example,
  onChange,
  onCancel,
  disabled,
  onlyLabel,
  displayedValueOffset = 0
}) => {
  const [opened, setOpened] = useState(false);
  const [durationValue, setDurationValue] = useState(value);

  useEffect(() => {
    setDurationValue(value);
  }, [value]);

  const [t] = useTranslation();

  const handleCancel = () => {
    setOpened(false);
    onCancel();
  };

  const handleConfirm = () => {
    onChange(durationValue);
    setOpened(false);
  };

  if (onlyLabel) {
    return <>{value ? formatTimer(value + displayedValueOffset) : 'XX:XX'}</>;
  }

  return (
    <Dropdown
      label={value ? formatTimer(value + displayedValueOffset) : 'XX:XX'}
      width={142}
      dropdownClassName={style.portal}
      withIcon={false}
      opened={opened}
      onOpenChange={setOpened}
      disabled={disabled}
      variation="inline"
    >
      <>
        {type === 'duration' && (
          <div className={style.duration}>
            <InputNumber
              value={durationValue?.toString() ?? ''}
              onChange={value => setDurationValue(parseIntOrNull(value))}
              incrementBy={1}
              className={style.input}
            />
            <div className={style.timerUnit}>{t('component-timer_duration_dropdown-sec')}</div>
          </div>
        )}
        {type === 'timer' && (
          <InputTime
            value={durationValue}
            onChange={value => setDurationValue(value)}
            exampleDuration={example}
            title={t(title)}
          />
        )}
      </>

      <div className={style.footer}>
        <Button onClick={handleCancel} variation="cancel" compact>
          {t('common-cancel')}
        </Button>
        <Button onClick={handleConfirm} variation="secondary" compact>
          {t('common-save')}
        </Button>
      </div>
    </Dropdown>
  );
};

const ControlledTimerDurationDropdownInput: FC<Omit<Props, 'onCancel'>> = ({
  onChange,
  value,
  ...props
}) => {
  const [timerValue, setTimerValue] = useState<number | null>(value ?? 0);

  const handleConfirm = (newValue: number | null) => {
    setTimerValue(newValue);
    onChange(newValue);
  };

  useEffect(() => {
    setTimerValue(value);
  }, [value]);

  return (
    <TimerDurationDropdownInput
      {...props}
      value={timerValue}
      onCancel={() => setTimerValue(0)}
      onChange={handleConfirm}
    />
  );
};

export { TimerDurationDropdownInput, ControlledTimerDurationDropdownInput };
